import type { BoardExportLayout } from './board-export-layout'
import { locateBoardExportLayout } from './board-export-layout'
import {
  IMPORT_FILE_MAX_CELLS,
  IMPORT_FILE_MAX_COLUMNS,
  IMPORT_FILE_MAX_ROWS,
  ImportFileTooLargeError,
} from './file-limits'

/**
 * Reads a worksheet for board-export detection (`locateBoardExportLayout`)
 * without ever walking its declared dimensions. Shared by the server parser
 * and the browser wizard so both see the same headers.
 *
 * A sheet's dimensions come from its farthest cell: a tiny file with values
 * in A1 and XFD20000 claims ~328M cells. The scan visits only rows and cells
 * that exist, reads at most IMPORT_FILE_MAX_COLUMNS columns of a row, keeps
 * blank rows positionally only inside the header search window (a run of
 * blank rows further down is collapsed to one, which the layout treats the
 * same way), and stops with ImportFileTooLargeError once the sheet is past
 * what an importable file can hold.
 *
 * The worksheet is described structurally (exceljs's Worksheet and Row fit),
 * so this package needs no spreadsheet dependency.
 */

export interface BoardScanRow {
  /** Highest column index that has a cell object (may be empty). */
  readonly cellCount: number
  /**
   * Highest column index holding a value, when the reader knows it (a
   * streaming reader keeps only the first IMPORT_FILE_MAX_COLUMNS columns
   * of a row, so `findCell` cannot look past them).
   */
  readonly filledWidth?: number
  findCell(colNumber: number): { value: unknown } | undefined
}

export interface BoardScanSheet {
  readonly rowCount: number
  eachRow(
    options: { includeEmpty: false },
    callback: (row: BoardScanRow, rowNumber: number) => void,
  ): void
}

export interface BoardExportRead {
  layout: BoardExportLayout
  /** Worksheet row number (1-based) for each layout grid index. */
  rowNumbers: number[]
}

/** Rows the layout inspects positionally: the title plus its header search. */
const LEADING_ROWS = 8
/**
 * Scan budgets. A board carries group banners, repeated header rows and
 * blank separators on top of its items, so the scan allows headroom over
 * the per-file limits; past these, no reading of the sheet can be imported.
 */
export const BOARD_SCAN_MAX_ROWS = IMPORT_FILE_MAX_ROWS * 2
export const BOARD_SCAN_MAX_CELLS =
  IMPORT_FILE_MAX_CELLS + IMPORT_FILE_MAX_ROWS * 2

function cellText(
  row: BoardScanRow,
  col: number,
  normalize: (value: unknown) => unknown,
): string {
  const cell = row.findCell(col)
  if (!cell) return ''
  return String(normalize(cell.value)).trim()
}

/**
 * The board layout of a sheet, or null for an ordinary table. Throws
 * ImportFileTooLargeError when the sheet (or its header row) is too large to
 * import — never silently truncating it.
 */
export function readBoardExportLayout(
  sheet: BoardScanSheet,
  normalize: (value: unknown) => unknown,
): BoardExportRead | null {
  const grid: string[][] = []
  const rowNumbers: number[] = []
  /** Grid index → the row (only rows with cells past the column cap). */
  const wideRows = new Map<number, BoardScanRow>()
  let cells = 0
  const push = (rowNumber: number, row: string[]) => {
    grid.push(row)
    rowNumbers.push(rowNumber)
    cells += Math.max(1, row.length)
    if (grid.length > BOARD_SCAN_MAX_ROWS)
      throw new ImportFileTooLargeError('rows')
    if (cells > BOARD_SCAN_MAX_CELLS) throw new ImportFileTooLargeError('cells')
  }
  const pushGap = (previous: number, next: number) => {
    for (let blank = previous + 1; blank < next; blank += 1) {
      push(blank, [])
      if (blank > LEADING_ROWS) break
    }
  }

  let lastRowNumber = 0
  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    pushGap(lastRowNumber, rowNumber)
    const width = Math.min(row.cellCount, IMPORT_FILE_MAX_COLUMNS)
    const values: string[] = []
    let lastFilled = -1
    for (let col = 1; col <= width; col += 1) {
      const text = cellText(row, col, normalize)
      values.push(text)
      if (text !== '') lastFilled = col - 1
    }
    if (
      row.cellCount > IMPORT_FILE_MAX_COLUMNS ||
      (row.filledWidth ?? 0) > IMPORT_FILE_MAX_COLUMNS
    )
      wideRows.set(grid.length, row)
    push(rowNumber, values.slice(0, lastFilled + 1))
    lastRowNumber = rowNumber
  })
  // Trailing blank rows still count toward the sheet's length.
  pushGap(lastRowNumber, sheet.rowCount + 1)

  const layout = locateBoardExportLayout(grid)
  if (!layout) return null

  // A header row wider than the cap would lose columns: refuse instead.
  const headerRow = wideRows.get(layout.headerRowIndex)
  if (headerRow?.filledWidth !== undefined) {
    if (headerRow.filledWidth > IMPORT_FILE_MAX_COLUMNS)
      throw new ImportFileTooLargeError('columns', headerRow.filledWidth)
  } else if (headerRow) {
    for (
      let col = IMPORT_FILE_MAX_COLUMNS + 1;
      col <= headerRow.cellCount;
      col += 1
    ) {
      if (cellText(headerRow, col, normalize) !== '')
        throw new ImportFileTooLargeError('columns')
    }
  }
  return { layout, rowNumbers }
}
